import { configure } from "safe-stable-stringify";
import { create } from "zustand";
import { devtools, persist, type StorageValue } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { CallbackState } from "../agent/types";

const safeStringify = configure({
    deterministic: false,
    maximumDepth: 10,
    strict: false,
});
interface CallbackStoreState {
    callbackState: CallbackState | null;
    /** Dials made so far in the session that dialled most recently. */
    sessionCallbackCount: { sessionId: string; count: number } | null;
}

export interface CallbackStore extends CallbackStoreState {
    setCallback: (callbackState: Omit<CallbackState, "sessionId">) => void;
    clearCallback: () => void;
    recordCallbackDialed: (sessionId: string) => void;
    /** Drops the count once its session ends, since a new session can reuse an ended one's id. */
    clearCallbackCount: (sessionId: string) => void;
}

/**
 * Callbacks already dialled in `sessionId`. The limit spans the whole session: each re-schedule originates
 * from the previous callback's task, so a per-task count restarts at 0 on every callback.
 */
export function callbackCountForSession(
    { sessionCallbackCount }: Pick<CallbackStoreState, "sessionCallbackCount">,
    sessionId: string | undefined,
): number {
    return sessionId && sessionCallbackCount?.sessionId === sessionId ? sessionCallbackCount.count : 0;
}

export const useCallbackStore = create<CallbackStore>()(
    immer(
        persist(
            devtools(
                (set) => ({
                    callbackState: null,
                    sessionCallbackCount: null,
                    recordCallbackDialed: (sessionId: string) =>
                        set(
                            (state) => {
                                state.sessionCallbackCount = {
                                    sessionId,
                                    count: callbackCountForSession(state, sessionId) + 1,
                                };
                            },
                            false,
                            "recordCallbackDialed",
                        ),
                    clearCallbackCount: (sessionId: string) =>
                        set(
                            (state) => {
                                if (state.sessionCallbackCount?.sessionId === sessionId) {
                                    state.sessionCallbackCount = null;
                                }
                            },
                            false,
                            "clearCallbackCount",
                        ),
                    setCallback: (callbackState: Omit<CallbackState, "sessionId">) =>
                        set(
                            (state) => {
                                state.callbackState = {
                                    ...callbackState,
                                    sessionId:
                                        callbackState.originalTask.sessionId ??
                                        callbackState.originalTask.sessionIdFallback,
                                };

                                return state;
                            },
                            false,
                            "setCallback",
                        ),
                    clearCallback: () =>
                        set(
                            (state) => {
                                state.callbackState = null;
                                return state;
                            },
                            false,
                            "clearCallback",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "callback",
                    name: "workspace/sdk",
                },
            ),
            {
                name: "callback-store",
                storage: {
                    getItem(name: string) {
                        const stringifiedState = localStorage.getItem(name);
                        if (!stringifiedState) {
                            return {
                                state: {
                                    callbackState: null,
                                } as CallbackStoreState,
                            } as StorageValue<CallbackStore>;
                        }

                        const parsedCallbackState = JSON.parse(stringifiedState) as CallbackStoreState;

                        return {
                            state: parsedCallbackState,
                        } as StorageValue<CallbackStore>;
                    },
                    setItem(name: string, value: StorageValue<CallbackStore>) {
                        localStorage.setItem(name, safeStringify(value.state));
                    },
                    removeItem: (name: string) => {
                        localStorage.removeItem(name);
                    },
                },
            },
        ),
    ),
);
